import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  clearStorageSubscription,
  updateStorageSubscription,
} from 'pages/Subscription/storageSubscription';
import useSubscriptionContext from 'pages/Subscription/context/useSubscriptionContext';
import useGetCurrenciesOptions from 'hooks/useGetCurrenciesOptions';
import ContentSuspense from 'common/ContentSuspense/ContentSuspense';
import ControlSelect from 'common/ControlSelect/ControlSelect';
import SubmitButton from 'common/SubmitButton/SubmitButton';
import schema from './SubscriptionSettingsForm.schema';
import { FormPayload } from './interfaces';
import dateFormatOptions from './dateFormatOptions';

const validate = zodResolver(schema);

export default function SubscriptionSettingsForm() {
  const { settingsForm } = useSubscriptionContext();
  const {
    currencyIdOptions,
    isError: currenciesError,
    isLoading: currenciesLoading,
  } = useGetCurrenciesOptions();
  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<FormPayload>({
    resolver: validate,
    mode: 'onChange',
    defaultValues: settingsForm,
  });

  const { dateFormat, currency } = watch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAddSettings = () => {
    // TODO: send data to the server when backend is ready
    clearStorageSubscription();
    navigate('/');
  };

  const disableSubmission = () =>
    !dateFormat || !currency || Object.values(errors).some((error) => error);

  useEffect(() => {
    const intervalId = setInterval(() => {
      updateStorageSubscription('settingsForm', watch());
    }, 3000);

    return () => clearInterval(intervalId);
  }, [watch]);

  return (
    <Stack width="68.8rem" mx="auto" my={8} spacing={8} alignItems="flex-start">
      <Stack spacing={2}>
        <Typography variant="h5">
          {t('subscription.settings_form_title')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {t('subscription.settings_form_subtitle')}
        </Typography>
      </Stack>
      <ContentSuspense isLoading={currenciesLoading} isError={currenciesError}>
        <form
          onSubmit={handleSubmit(handleAddSettings)}
          noValidate
          style={{ width: '100%' }}
        >
          <FormControl
            fullWidth
            sx={{ display: 'flex', flexDirection: 'column', rowGap: 3 }}
          >
            <ControlSelect
              control={control}
              name="dateFormat"
              errors={errors}
              label="label.date_format"
              selectOptions={dateFormatOptions}
              required
            />
            <ControlSelect
              control={control}
              name="currency"
              errors={errors}
              label="clients.currency"
              selectOptions={currencyIdOptions}
              required
            />
          </FormControl>
          <SubmitButton
            label="subscription.explore_flowtly"
            sx={{ height: '4.74rem', px: 2.75, my: 8 }}
            disabled={disableSubmission()}
            noIcon
          />
        </form>
      </ContentSuspense>
    </Stack>
  );
}
